/**
 * World construction and the fixed-timestep clock.
 *
 * The world is a plain mutable container (see core/types.ts for the shape).
 * Systems mutate it in a fixed order defined by `main.ts`; nothing here knows
 * about Three.js, so the whole simulation is testable in Node.
 */

import { SIM, VIEW, ECONOMY } from '@/core/config';
import { seedFrom } from '@/core/rng';
import type { World, Phase } from '@/core/types';

export function createWorld(seed = 'pixel-td'): World {
  return {
    phase: 'boot' as Phase,
    time: 0,
    round: 0,
    cash: ECONOMY.startingCash,
    lives: ECONOMY.startingLives,
    speed: 1,

    enemies: [],
    towers: [],
    projectiles: [],
    pending: [],

    fx: { particles: [], shockwaves: [], shake: 0, numbers: [] },

    track: {
      points: new Float32Array(0),
      length: 0,
      tangents: new Float32Array(0),
      width: new Float32Array(0),
    },

    buildable: new Uint8Array(VIEW.cols * VIEW.rows),

    rng: seedFrom(seed),
    tick: 0,
  };
}

/**
 * Screen shake.
 *
 * Roughly ten sites add trauma linearly — every explosion, every enemy death,
 * every leak, every heavy tower firing. At round 45 a hundred-odd deaths land
 * in the same frame and the raw sum ran far past anything readable, so the
 * whole tube juddered continuously. A playtester called it "unpleasant ultra
 * shake" and they were right.
 *
 * The accumulator stays linear because callers are spread across subsystems
 * that should not have to know about each other. Saturation happens once, at
 * read time, through an exponential soft-knee: one death still reads as a
 * kick, a hundred reads as a big kick rather than as an earthquake.
 */
export const SHAKE = {
  /** Hard ceiling in buffer pixels, whatever the raw trauma sum reaches. */
  max: 4.5,
  /** Raw trauma at which output reaches ~63% of `max`. */
  knee: 6.0,
  /** Per-step decay. */
  decay: 0.86,
  /** Below this the offset would round to zero anyway. */
  epsilon: 0.01,
  /** Stops a single catastrophic frame from ringing for seconds afterward. */
  maxTrauma: 40,
} as const;

/**
 * Converts raw accumulated trauma into a bounded pixel amplitude.
 * Read this rather than `world.fx.shake` directly.
 */
export function shakeAmplitude(world: World): number {
  const raw = world.fx.shake;
  if (raw <= 0) return 0;
  return SHAKE.max * (1 - Math.exp(-raw / SHAKE.knee));
}

/** Clears the per-frame effect queues. Called after the render layer drains them. */
export function clearFx(world: World): void {
  world.fx.particles.length = 0;
  world.fx.shockwaves.length = 0;
  world.fx.numbers.length = 0;

  // Clamp before decay: an unbounded sum would take many frames to fall back
  // through the knee and would keep shaking long after the event that caused it.
  if (world.fx.shake > SHAKE.maxTrauma) world.fx.shake = SHAKE.maxTrauma;
  world.fx.shake *= SHAKE.decay;
  if (world.fx.shake < SHAKE.epsilon) world.fx.shake = 0;
}

/**
 * Fixed-step accumulator.
 *
 * Rendering runs at display rate and interpolates; the simulation always
 * advances in whole 1/60 steps so ballistics, flocking and wave timing are
 * frame-rate independent and reproducible from a seed.
 */
export class Clock {
  private acc = 0;
  private last = 0;
  /** Interpolation factor for the renderer, 0..1 between the last two steps. */
  alpha = 0;
  /** Steps taken on the most recent frame, for the debug overlay. */
  lastSteps = 0;

  reset(now: number): void {
    this.last = now;
    this.acc = 0;
  }

  /** Returns how many sim steps to run this frame. */
  advance(now: number, speed: number): number {
    let frame = (now - this.last) / 1000;
    this.last = now;
    // Guard against tab-restore and breakpoint stalls.
    if (frame > 0.25) frame = 0.25;
    this.acc += frame * speed;

    let steps = 0;
    while (this.acc >= SIM.dt && steps < SIM.maxStepsPerFrame * speed) {
      this.acc -= SIM.dt;
      steps++;
    }
    // Drop the backlog rather than accumulating debt we can never repay.
    if (this.acc > SIM.dt * 4) this.acc = 0;

    this.alpha = this.acc / SIM.dt;
    this.lastSteps = steps;
    return steps;
  }
}
